import { IWorkerAssignment } from '@company/types/workerAssignment';
import {
  DropdownListItem,
  DropdownListItemEmpty,
} from '@components/elements/DropdownList';
import DATE_FORMATS from '@constants/dateFormat';
import QueryKeys from '@constants/queryKeys';
import { UserRole } from '@constants/userRoles';
import { QueryClient } from '@tanstack/react-query';
import { IActiveJob, IActiveJobs } from '@th-types/active-jobs.type';
import { JobAssignmentStatus } from '@th-types/job-assignment-status.type';
import IJobDetail, { ShiftPosition } from '@th-types/job.detail.type';
import { PayRatePeriod } from '@th-types/payRatePeriod';
import { ITag } from '@th-types/tag.type';
import { IWorkerData } from '@th-types/worker.type';
import { IWorkerJobData } from '@th-types/workerProfile.type';
import {
  convertToTimezone,
  createDateTimeFromDifferentDates,
  formatLocalDate,
  isSameDateOrAfter,
  UTC_TIMEZONE,
} from '@utils/DateUtils';
import { getWorkerTags } from '@utils/Person';
import { addDays, format } from 'date-fns';

const SERVER_URL = import.meta.env.VITE_SERVER_BASE_URL;

/**
 * Generate worker profile URL for sharing
 */
export const getWorkerProfileUrl = (worker: IWorkerData): string => {
  return `${SERVER_URL}/w/${worker.customUrl ?? worker.id}`;
};

/**
 * Generate worker endorsement URL
 */
export const getWorkerEndorsementUrl = (worker: IWorkerData): string => {
  return `${SERVER_URL}/w/${worker.customUrl ?? worker.id}?openEndorsementModal=true`;
};

/**
 * Get worker tags including hired status
 */
export const getWorkerTagsWithHired = (
  worker: IWorkerData,
  matchSkills?: boolean,
  hired?: boolean
): ITag[] => {
  const tags = getWorkerTags(worker, matchSkills);
  if (hired) {
    tags.push({
      label: 'HIRED',
      bg: 'primary',
      bgText: 'light',
    });
  }
  return tags;
};

/**
 * Check if user is internal member of a job
 */
export const isInternalMember = (
  memberId: number,
  job: IJobDetail
): boolean => {
  return (
    job.members?.some(
      (member: any) => member.id === memberId && member.isInternal
    ) || false
  );
};

/**
 * Format job date range
 */
export const formatJobDateRange = (job: IJobDetail): string => {
  const formattedStartDate = formatLocalDate(
    job.localizedStart,
    'MMM dd',
    UTC_TIMEZONE
  );

  if (job.localizedEnd) {
    const formattedEndDate = formatLocalDate(
      job.localizedEnd,
      'MMM dd',
      UTC_TIMEZONE
    );
    const formattedYear = formatLocalDate(job.localizedEnd, 'yy', UTC_TIMEZONE);
    return `${formattedStartDate} - ${formattedEndDate} '${formattedYear}`;
  }

  return `${formattedStartDate} - on going`;
};

/**
 * Get job location text
 */
export const getJobLocationText = (job: IJobDetail): string => {
  if (job.nationalJob) {
    return 'National Job';
  }
  return [
    job.address.line1,
    job.address.city,
    job.address.state?.ansiAbbreviation,
  ]
    .filter((a) => a !== null)
    .join(', ');
};

/**
 * Check if decline button should be shown
 */
export const shouldShowDeclineButton = (
  jobApplication: any,
  status?: JobAssignmentStatus
): boolean => {
  return (
    jobApplication &&
    (status === JobAssignmentStatus.WORKER_INTERESTED ||
      status === JobAssignmentStatus.WORKER_APPLIED)
  );
};

/**
 * Check if chat button should be shown
 */
export const shouldShowChatButton = (
  memberId: number,
  job: IJobDetail,
  hasRole: (role: UserRole) => boolean,
  isActive: boolean,
  jobAssignmentId?: number,
  jobId?: number
): boolean => {
  // If we're in a job context (jobId provided), show chat button regardless of internal member status
  if (jobId) {
    return true;
  }

  // Original logic for non-job contexts
  return (
    (isInternalMember(memberId, job) || hasRole(UserRole.ROLE_COMPANY_ADMIN)) &&
    (isActive || !!jobAssignmentId)
  );
};

/**
 * Check if reconsider button should be shown
 */
export const shouldShowReconsiderButton = (
  jobAssignmentLogs: any[] | undefined,
  canBeReconsidered: (logs: any[]) => boolean
): boolean => {
  return !!(jobAssignmentLogs && canBeReconsidered(jobAssignmentLogs));
};

/**
 * Get reconsider action details
 */
export const getReconsiderActionDetails = (
  jobAssignmentLogs: any[] | undefined,
  reconsiderJobOfferAction: () => void,
  reconsiderWorker: () => void
) => {
  const logs = jobAssignmentLogs;
  const isOfferReconsider =
    logs && logs[0].fromStatus === JobAssignmentStatus.OFFERED;

  return {
    action: isOfferReconsider ? reconsiderJobOfferAction : reconsiderWorker,
    title: isOfferReconsider ? 'Reconsider offer' : 'Reconsider worker',
    message: isOfferReconsider
      ? 'This action will reconsider the offer for this worker.'
      : 'This action will reconsider this worker as applicant, and you can hire them.',
  };
};

/**
 * Get drawer props for consistent styling
 */
export const getDrawerProps = (show: boolean) => ({
  opened: show,
  position: 'right' as const,
  withCloseButton: false,
});

/**
 * Get drawer size based on device
 */
export const getDrawerSize = (isMobile: boolean): string => {
  return isMobile ? '90%' : '85%'; // Adjust sizes as it was in prod previously
};

/**
 * Get worker tags for tabs component
 */
export const getWorkerTagsForTabs = (
  worker: IWorkerData,
  matchSkills?: boolean,
  hired?: boolean
): ITag[] => {
  const tags = getWorkerTags(worker, matchSkills);
  if (hired) {
    tags.push({
      label: 'HIRED',
      bg: 'primary',
      bgText: 'light',
    });
  }
  return tags;
};

/**
 * Get worker activity data for tabs component
 */
export const getWorkerActivityForTabs = (
  workerData: IWorkerJobData
): IWorkerAssignment => {
  return workerData?.jobAssignment && workerData?.jobAssignmentLogs
    ? {
        thJobAssignment: workerData.jobAssignment,
        thJobAssignmentLogList: workerData.jobAssignmentLogs,
      }
    : ({} as IWorkerAssignment);
};

/**
 * Get job role and company info text for job description
 */
export const getJobRoleAndCompanyInfo = (
  job: IJobDetail,
  isHired: boolean
): { position: string; companyName: string } | null => {
  if (isHired) {
    return {
      position: job.position,
      companyName: job.company.name,
    };
  }
  return null;
};

/**
 * Format shift data for update API call
 */
export const formatShiftUpdateData = (
  shift: ShiftPosition,
  formData: {
    date: Date | null;
    positionId: number;
    positionName: string;
    wage: number;
    payRatePeriod: string;
    startTime: Date | null;
    endTime: Date | null;
    paidBreak: string;
  }
) => {
  if (!formData.date || !formData.startTime || !formData.endTime) {
    throw new Error('Missing required date/time data');
  }

  const startDateTime = createDateTimeFromDifferentDates(
    formData.date,
    formData.startTime
  );
  let endDateTime = createDateTimeFromDifferentDates(
    formData.date,
    formData.endTime
  );

  if (isSameDateOrAfter(startDateTime, endDateTime)) {
    endDateTime = addDays(endDateTime, 1);
  }

  const startFormatted = format(startDateTime, DATE_FORMATS.DATE_TIME_FORMAT);
  const endFormatted = format(endDateTime, DATE_FORMATS.DATE_TIME_FORMAT);

  const payRatePeriod =
    formData.payRatePeriod === 'daily' ? PayRatePeriod.DAY : PayRatePeriod.HOUR;

  const payRateString = formData.wage
    ? formData.wage.toString().replace('$', '').trim()
    : '0';
  return {
    id: shift.id,
    position: formData.positionId,
    payRate: payRateString,
    payRatePeriod,
    breakPaid: formData.paidBreak === 'yes',
    start: startFormatted,
    end: endFormatted,
    localizedStart: startDateTime.toISOString(),
    localizedEnd: endDateTime.toISOString(),
    openPositions: 1,
    openBackups: 0,
    isNextDay: isSameDateOrAfter(startDateTime, endDateTime),
  };
};

/**
 * Populate edit form data from shift
 */
export const populateEditFormData = (
  shift: ShiftPosition,
  jobTimezone?: string
) => {
  const timezone = jobTimezone || 'UTC';

  // Use the existing convertToTimezone utility function for consistency
  const startDate = convertToTimezone(shift.start, timezone);
  const endDate = convertToTimezone(shift.end, timezone);

  const payRatePeriod = shift.wage.payRatePeriod?.toLowerCase() || 'hourly';
  const validPayRatePeriod = ['hourly', 'daily'].includes(payRatePeriod)
    ? payRatePeriod
    : 'hourly';
  const dateOnly = new Date(
    startDate.getFullYear(),
    startDate.getMonth(),
    startDate.getDate()
  );
  const startTimeOnly = new Date(
    2000,
    0,
    1,
    startDate.getHours(),
    startDate.getMinutes()
  );
  const endTimeOnly = new Date(
    2000,
    0,
    1,
    endDate.getHours(),
    endDate.getMinutes()
  );

  return {
    date: dateOnly,
    positionId: shift.title.id,
    positionName: shift.title.name,
    wage: shift.wage.payRate,
    payRatePeriod: validPayRatePeriod,
    startTime: startTimeOnly,
    endTime: endTimeOnly,
    paidBreak: shift.breakPaid ? 'yes' : 'no',
  };
};

/**
 * Share button configuration types
 */
export interface ShareButtonConfig {
  showButton: boolean;
  type: 'specific' | 'dropdown' | 'none';
  jobId?: number;
  buttonText: string;
}

/**
 * Get share button configuration based on context
 */
export const getShareButtonConfig = (
  workerData: IWorkerJobData,
  jobId?: number
): ShareButtonConfig => {
  // Scenario 1: Job-specific context (jobId provided)
  if (jobId) {
    // Check if jobAssignment already exists (already shared/applied/offered/hired)
    if (workerData.jobAssignment) {
      return {
        showButton: false,
        type: 'none',
        buttonText: '',
      };
    }

    return {
      showButton: true,
      type: 'specific',
      jobId,
      buttonText: 'SHARE JOB',
    };
  }

  // Scenario 2: General context (no jobId provided)
  return {
    showButton: true,
    type: 'dropdown',
    buttonText: 'SHARE',
  };
};

/**
 * Format active job for dropdown list
 */
export const formatActiveJobForDropdown = (
  job: IActiveJob,
  shareJobToWorker: (jobId: number, workerId: number) => void,
  workerId: number
): DropdownListItem => {
  const getJobLabel = (jobData: IActiveJob) =>
    `${jobData.position} - ${jobData.address?.city}/${
      jobData.address?.state.ansiAbbreviation ?? ''
    }`;

  const getJobKey = (jobData: IActiveJob) =>
    `${jobData.position} ${jobData.address?.city} ${
      jobData.address?.state.ansiAbbreviation ?? ''
    }`;

  return {
    header: job.eventName,
    label: getJobLabel(job),
    key: getJobKey(job),
    id: job.id,
    action: () => shareJobToWorker(job.id, workerId),
  };
};

/**
 * Filter active jobs to exclude those already shared with worker
 */
export const filterAvailableJobsForSharing = (
  activeJobs: IActiveJobs[]
): IActiveJobs[] => {
  return activeJobs.filter((jobData) => {
    // Filter out jobs that already have jobAssignment with this worker
    // This includes jobs with SHARED, APPLIED, OFFERED, HIRED, etc. status
    return !jobData.jobAssignment;
  });
};

/**
 * Get empty state message for dropdown when no jobs available
 */
export const getNoJobsAvailableMessage = (): DropdownListItemEmpty => {
  return {
    label: 'No published jobs to share',
  };
};

/**
 * Invalidate worker-related queries to refresh worker data
 */
export const invalidateWorkerQueries = ({
  queryClient,
  workerId,
  jobId,
  jobAssignmentId,
  skipJobCounters = false,
}: {
  queryClient: QueryClient;
  workerId: number;
  jobId?: number;
  jobAssignmentId?: number;
  skipJobCounters?: boolean;
}) => {
  // Use the same pattern as WorkerInfoSection (favorite/block mutations) which works correctly
  const targetQueryKey = `${QueryKeys.WORKER_PROFILE}_${workerId}_${jobId || 'no-job'}_triggerProfileView`;
  const jobAssignmentQueryKey = `${QueryKeys.WORKER_PROFILE}_jobAssignment_${jobAssignmentId}`;

  // Invalidate all the queries using Promise.all like WorkerInfoSection
  Promise.all([
    // Specific worker profile query with correct format
    queryClient.invalidateQueries({
      queryKey: [targetQueryKey],
    }),
    // Job assignment query if exists
    ...(jobAssignmentId
      ? [
          queryClient.invalidateQueries({
            queryKey: [jobAssignmentQueryKey],
          }),
        ]
      : []),
    // Worker profile queries (general)
    queryClient.invalidateQueries({
      queryKey: [QueryKeys.WORKER_PROFILE],
    }),
    // Job assignment queries (general)
    queryClient.invalidateQueries({
      queryKey: [QueryKeys.WORKER_ASSIGNMENT_JOB],
    }),
    // Job counters (for updated counts) - skip if requested
    ...(skipJobCounters
      ? []
      : [
          queryClient.invalidateQueries({
            queryKey: [QueryKeys.COMPANY_JOB_COUNTERS],
          }),
        ]),
    // Job shifts data (crucial for JobWorkerShifts component to update)
    ...(jobId
      ? [
          queryClient.invalidateQueries({
            queryKey: [QueryKeys.COMPANY_JOB_SHIFTS, jobId],
          }),
        ]
      : []),
    // DetailV2 candidates panel: tab lists + counters
    queryClient.invalidateQueries({
      queryKey: [QueryKeys.JobDetail.CANDIDATES],
    }),
    queryClient.invalidateQueries({
      queryKey: [QueryKeys.JobDetail.CANDIDATE_COUNTERS],
    }),
  ]);
};
